import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import StatusBadge from "@/components/StatusBadge";
import { AlertTriangle, Inbox } from "lucide-react";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    api.get("/bookings").then(({ data }) => setBookings(data));
    api.get("/customers/me/leads").then(({ data }) => setLeads(data)).catch(() => {});
  }, []);

  const openLeads = leads.filter((l) => !["completed","cancelled","paid_out"].includes(l.status));
  const paidBookings = bookings.filter((b) => b.payment_status === "paid" || b.status === "completed");

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="font-display text-3xl font-bold">Hi {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-slate-600 mt-1">Your requests, quotes and bookings — all in one place.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/request" data-testid="dash-find-help" className="btn-accent">Find help</Link>
          <Link to="/emergency" data-testid="dash-emergency" className="btn-outline text-rose-700 border-rose-200">Need help now</Link>
        </div>

        <div className="mt-10">
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><Inbox size={18}/> My requests</h2>
          <div className="mt-3 space-y-3">
            {leads.length === 0 && (
              <div className="card text-slate-600 text-sm">
                You haven't requested any services yet. <Link to="/request" className="text-emerald-600 font-semibold">Start a new request</Link>.
              </div>
            )}
            {leads.map((l) => (
              <Link
                key={l.id}
                to={`/my-request/${l.id}`}
                data-testid={`lead-item-${l.id}`}
                className="card flex items-center justify-between hover:-translate-y-1 transition-transform"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {l.urgent && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-600 text-white text-[10px] font-bold"><AlertTriangle size={10}/> URGENT</span>}
                    <span className="text-xs text-slate-500">{l.ref}</span>
                  </div>
                  <div className="font-semibold text-slate-900 mt-1 truncate">{l.category} · {l.postcode}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-1">{l.description}</div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <StatusBadge status={l.status} />
                  <div className="text-xs text-slate-600">
                    <b data-testid={`lead-quotes-${l.id}`}>{l.quotes_received}</b> quotes
                    <span className="text-slate-400"> · {l.providers_notified} notified</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="font-display text-xl font-bold">My bookings</h2>
          <div className="mt-3 space-y-3">
            {bookings.length === 0 && <div className="card text-slate-600 text-sm">No bookings yet — bookings appear here once you've selected a provider and completed payment.</div>}
            {bookings.map((b) => (
              <Link key={b.id} to={`/bookings/${b.id}`} data-testid={`booking-item-${b.id}`} className="card flex items-center justify-between hover:-translate-y-1 transition-transform">
                <div>
                  <div className="font-semibold text-slate-900">{b.provider_name}</div>
                  <div className="text-sm text-slate-600 line-clamp-1">{b.description}</div>
                  <div className="text-xs text-slate-500 mt-1">{b.preferred_date} · {b.postcode}</div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <StatusBadge status={b.status} />
                  {b.quote_amount && <div className="text-sm font-semibold">£{b.quote_amount.toFixed(2)}</div>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
